import React from 'react';
import { Download, Loader2, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { bsiInternetBankApi } from '@/lib/banks/bsi/api';
import { downloadBsiReceipt, downloadBsiReceiptByTrack } from '@/lib/banks/bsi/download';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

const BsiReceiptTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [trackNumber, setTrackNumber] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [receiptKey, setReceiptKey] = React.useState<string | null>(null);
  const [fields, setFields] = React.useState<Record<string, string>>({});

  const handleLookup = async () => {
    if (!trackNumber.trim()) {
      onMessage({ type: 'error', text: 'شماره پیگیری را وارد کنید.' });
      return;
    }
    setLoading(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.transferReceiptLookup({ track_number: trackNumber.trim() });
      setReceiptKey(res.data.receipt_key);
      setFields(res.data.fields || {});
      onMessage({ type: 'success', text: res.message || 'رسید دریافت شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'دریافت رسید ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoading(false);
    }
  };

  const entries = Object.entries(fields);

  return (
    <Card className="lg:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Receipt className="h-4 w-4" />
          رسید انتقال
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!sessionValid ? (
          <p className="text-sm text-amber-700">برای دریافت رسید ابتدا وارد اینترنت‌بانک شوید.</p>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              مثل سایت بانک، با شماره پیگیری می‌توانید رسید را ببینید و PDF یا اکسل دانلود کنید.
            </p>
            <div className="space-y-2">
              <Label>شماره پیگیری</Label>
              <Input value={trackNumber} onChange={(e) => setTrackNumber(e.target.value)} dir="ltr" className="font-mono" placeholder="مثلاً 123456789" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={() => void handleLookup()} disabled={loading}>
                {loading ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                استعلام رسید
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!trackNumber.trim()}
                onClick={() =>
                  void downloadBsiReceiptByTrack(trackNumber.trim(), 'pdf').catch((e) =>
                    onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                  )
                }
              >
                <Download className="ml-1 h-4 w-4" />
                PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!trackNumber.trim()}
                onClick={() =>
                  void downloadBsiReceiptByTrack(trackNumber.trim(), 'excel').catch((e) =>
                    onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                  )
                }
              >
                <Download className="ml-1 h-4 w-4" />
                اکسل
              </Button>
              {receiptKey && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    void downloadBsiReceipt(receiptKey, 'html').catch((e) =>
                      onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }),
                    )
                  }
                >
                  <Download className="ml-1 h-4 w-4" />
                  HTML
                </Button>
              )}
            </div>
            {entries.length > 0 && (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {entries.map(([key, value]) => (
                  <div key={key} className="rounded border bg-slate-50 px-3 py-2 text-sm">
                    <div className="text-xs text-slate-500">{key}</div>
                    <div>{value}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BsiReceiptTab;
